import type { ComponentType } from "react";
import { Bean, Egg, Fish, Leaf, Milk, Nut, Shell, Sprout, Wheat } from "lucide-react";
import { useRecipeAllergens } from "@/hooks/recipes/queries";
import type { MaterialAllergen } from "@/types/material";
import type { RecipeIngredient } from "@/types/recipe";

/** The glyph for each built-in allergen, by `Allergen.code`. */
const ALLERGEN_ICONS: Record<string, ComponentType<{ className?: string; "aria-hidden"?: boolean }>> = {
  MILK: Milk,
  EGGS: Egg,
  FISH: Fish,
  SHELLFISH: Shell,
  MOLLUSKS: Shell,
  TREE_NUTS: Nut,
  PEANUTS: Nut,
  WHEAT: Wheat,
  GLUTEN: Wheat,
  SOY: Bean,
  LUPIN: Bean,
  SESAME: Sprout,
  MUSTARD: Sprout,
  CELERY: Leaf,
};

export interface AllergenRef {
  code: string;
  name: string;
}

interface AllergenSymbolProps {
  allergen: AllergenRef;
}

/**
 * One allergen as a small symbol, named for screen readers and on hover. A code
 * with no glyph here — a tenant's own allergen, or a built-in with no good
 * picture such as sulfites — shows the allergen's initial instead, so it is
 * never silently left off.
 */
export function AllergenSymbol({ allergen }: AllergenSymbolProps) {
  const Icon = ALLERGEN_ICONS[allergen.code];
  return (
    <span
      role="img"
      aria-label={allergen.name}
      title={allergen.name}
      className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-(--color-warning-bg) text-(--color-warning-text) print:size-4"
    >
      {Icon ? (
        <Icon aria-hidden className="size-3 print:size-2.5" />
      ) : (
        <span aria-hidden className="text-[0.625rem] leading-none font-bold uppercase">
          {allergen.name.charAt(0)}
        </span>
      )}
    </span>
  );
}

/** A row of symbols; nothing at all when there are no allergens. */
export function AllergenSymbols({ allergens }: { allergens: AllergenRef[] }) {
  if (allergens.length === 0) return null;
  return (
    <span className="inline-flex items-center gap-1 align-middle">
      {allergens.map((allergen) => (
        <AllergenSymbol key={allergen.code} allergen={allergen} />
      ))}
    </span>
  );
}

interface AllergenSource {
  allergens?: MaterialAllergen[];
  subIngredients?: { childMaterial?: AllergenSource | null }[];
}

/**
 * Allergens an ingredient carries through its own links and through the
 * sub-ingredients it is made of, once each.
 */
export function materialAllergens(material: AllergenSource | null | undefined): AllergenRef[] {
  const found = new Map<string, AllergenRef>();
  const walk = (node: AllergenSource | null | undefined) => {
    if (!node) return;
    for (const link of node.allergens ?? []) {
      if (link.allergen?.code) found.set(link.allergen.code, { code: link.allergen.code, name: link.allergen.name });
    }
    for (const sub of node.subIngredients ?? []) walk(sub.childMaterial);
  };
  walk(material);
  return [...found.values()];
}

/**
 * The allergen symbols for one recipe line: its ingredient's, or — for a line
 * that is another recipe — that recipe's, read from `GET /recipes/:id/allergens`.
 */
export function LineAllergens({ line }: { line: Pick<RecipeIngredient, "material" | "subRecipeId"> }) {
  if (line.subRecipeId != null) return <SubRecipeAllergens recipeId={line.subRecipeId} />;
  return <AllergenSymbols allergens={materialAllergens(line.material)} />;
}

function SubRecipeAllergens({ recipeId }: { recipeId: number }) {
  const { data } = useRecipeAllergens(recipeId);
  return <AllergenSymbols allergens={data ?? []} />;
}
